// api/upload.js — 파일 업로드의 **규칙**. 네트워크는 `client.js` 가 하고 여기는 판단만 한다.
//
// 계약 정본: `docs/shared-file-api-v0.1.md`. 업로드는 한 번의 POST 가 아니라 **세 걸음**이다.
//
//   ① 예약(reserve)   JSON — 이름·형식·크기·SHA-256 을 미리 말한다
//   ② 전송(transfer)  PUT  — 바이트 그대로. base64 도 multipart 도 아니다
//   ③ 확정(finalize)  POST — 객체가 확정되고 **불변 판**이 생긴다
//   (그리고 Task 에 붙이는 ④ attach 는 그 뒤의 별개 행위다)
//
// 이 파일이 존재하는 이유는 **재시도의 자리**다. 세 걸음이 각자 실패하고, 실패한 걸음만
// 다시 해야 한다:
//
//   · ② 가 실패하면 예약은 살아 있다 → **② 만** 다시. 다시 예약하면 할당량을 두 번 먹는다.
//   · ③ 이 성공하고 ④ 가 실패하면 판은 **이미 만들어졌다** → 다시 올리면 같은 파일의 판이
//     하나 더 생긴다. 그건 재시도가 아니라 중복이다. 그래서 그 자리에서는 **붙이기만** 다시 한다.
//
// 그 판단을 화면 안에 두면 분기마다 조금씩 달라진다. 여기 순수 함수 하나로 둔다.

/** 업로드가 지나는 자리. 이 밖의 값은 없다. */
export const UPLOAD_PHASE = Object.freeze({
  IDLE: 'idle',
  DIGESTING: 'digesting',
  RESERVING: 'reserving',
  TRANSFERRING: 'transferring',
  FINALIZING: 'finalizing',
  ATTACHING: 'attaching',
  DONE: 'done',
  ERROR: 'error',
});

/** 다시 시작할 수 있는 걸음. `null` 이면 처음부터다. */
export const UPLOAD_STEP = Object.freeze({
  RESERVE: 'reserve',
  TRANSFER: 'transfer',
  FINALIZE: 'finalize',
  ATTACH: 'attach',
});

export function emptyUploadState() {
  return {
    phase: UPLOAD_PHASE.IDLE,
    /** 고른 파일의 **성질만**. 바이트는 여기 들어오지 않는다. */
    file: null,
    uploadId: null,
    checksumSha256: null,
    /** 논리적 행위 하나에 붙는 멱등 키. **재시도해도 바꾸지 않는다** —
     *  바꾸면 서버에게는 새 요청이고, 예약이나 판이 하나 더 생긴다. */
    reserveKey: null,
    finalizeKey: null,
    /**
     * **예약에 실제로 보낸 값.**
     *
     * 왜 따로 들고 있나: 예약 응답이 그물에서 사라졌는데(네트워크 실패) 그사이 사람이
     * 이름을 고치면, 같은 멱등 키로 **다른 본문**이 나간다. 서버는 그것을 키 재사용으로
     * 보고 영원히 거절하고, 그 자리는 다시는 진행되지 않는다. 그래서 한 번 정한 예약 본문은
     * 그 예약이 끝나거나 취소될 때까지 그대로 쓴다.
     */
    reservedIntent: null,
    /** 예약이 알려 준 다음 걸음의 경로. 다시 열었을 때도 같은 자리로 이어 간다. */
    contentPath: null,
    finalizePath: null,
    transferred: false,
    /** 확정된 manifest. 있으면 판은 **이미 존재한다** — 다시 올리지 않는다. */
    manifest: null,
    attached: false,
    loaded: 0,
    error: null,
  };
}

/** 파일에서 들고 다닐 성질. `File` 객체 자체는 상태에 넣지 않는다. */
export function describeFile(file) {
  if (!file) return null;
  return { name: file.name ?? '', size: Number(file.size ?? 0), type: file.type ?? '' };
}

/**
 * 상태 전이. **순수 함수다** — 같은 상태·같은 사건이면 언제나 같은 결과다.
 *
 * 모르는 사건은 상태를 **그대로 돌려준다**. 던지지 않는 이유: 전이표의 구멍 때문에 사람이
 * 올리던 파일이 화면에서 사라지는 것보다, 아무 일도 일어나지 않는 편이 낫다.
 */
export function advanceUpload(state, event) {
  const current = state ?? emptyUploadState();
  switch (event?.type) {
    case 'select':
      // 새 파일을 고르면 앞의 업로드는 **없던 일이 아니다** — 다만 이 자리에서는 놓는다.
      // (예약을 정리하는 것은 화면의 abort 호출이고, 그건 이 전이와 별개다.)
      return { ...emptyUploadState(), file: describeFile(event.file) };
    case 'clear':
      return emptyUploadState();
    case 'digesting':
      return { ...current, phase: UPLOAD_PHASE.DIGESTING, error: null };
    case 'reserving':
      return {
        ...current,
        phase: UPLOAD_PHASE.RESERVING,
        reserveKey: event.key ?? current.reserveKey,
        // 보내기 **전에** 굳힌다. 응답을 못 받아도 무엇을 보냈는지는 남아 있어야 한다.
        reservedIntent: event.intent ?? current.reservedIntent,
        error: null,
      };
    case 'reserved':
      return {
        ...current,
        phase: UPLOAD_PHASE.TRANSFERRING,
        uploadId: event.uploadId ?? current.uploadId,
        checksumSha256: event.checksumSha256 ?? current.checksumSha256,
        contentPath: event.contentPath ?? current.contentPath,
        finalizePath: event.finalizePath ?? current.finalizePath,
        transferred: false,
        loaded: 0,
        error: null,
      };
    case 'progress':
      return { ...current, phase: UPLOAD_PHASE.TRANSFERRING, loaded: Number(event.loaded ?? 0), error: null };
    case 'transferred':
      return { ...current, phase: UPLOAD_PHASE.FINALIZING, transferred: true, error: null };
    case 'finalizing':
      return {
        ...current,
        phase: UPLOAD_PHASE.FINALIZING,
        finalizeKey: event.key ?? current.finalizeKey,
        error: null,
      };
    case 'finalized':
      return {
        ...current,
        phase: UPLOAD_PHASE.ATTACHING,
        transferred: true,
        manifest: event.manifest ?? null,
        error: null,
      };
    case 'attached':
      return { ...current, phase: UPLOAD_PHASE.DONE, attached: true, error: null };
    case 'failed':
      return { ...current, phase: UPLOAD_PHASE.ERROR, error: { ...event.error, step: event.step ?? null } };
    case 'interrupted':
      // 화면이 사라져서 전송이 끊겼다(대화상자를 닫았다). **예약은 그대로 둔다** —
      // 다시 열면 ② 만 이어서 하면 되고, 새로 예약하면 할당량을 두 번 먹는다.
      return { ...current, phase: UPLOAD_PHASE.IDLE, loaded: 0, error: null };
    case 'cancelled':
      // 취소는 실패가 아니다. 고른 파일은 남기고 진행만 되돌린다 — 다시 누르면 된다.
      return {
        ...current,
        phase: UPLOAD_PHASE.IDLE,
        uploadId: null,
        checksumSha256: null,
        // 예약을 놓았으므로 그 키도, 그때 보낸 본문도 놓는다. 같은 키로 다시 예약하면
        // 서버는 **닫힌 예약**을 그대로 돌려주고, 그 위에서 ② 는 영원히 실패한다.
        reserveKey: null,
        finalizeKey: null,
        reservedIntent: null,
        contentPath: null,
        finalizePath: null,
        transferred: false,
        loaded: 0,
        error: null,
      };
    default:
      return current;
  }
}

/**
 * 지금 다시 누르면 **어디서부터** 하나.
 *
 * 이 함수 하나가 「확정된 판을 다시 올리지 않는다」를 지킨다.
 */
export function resumeStep(state) {
  const current = state ?? emptyUploadState();
  if (current.manifest && !current.attached) return UPLOAD_STEP.ATTACH;
  if (current.manifest) return null;
  if (current.uploadId && current.transferred) return UPLOAD_STEP.FINALIZE;
  if (current.uploadId) return UPLOAD_STEP.TRANSFER;
  return UPLOAD_STEP.RESERVE;
}

/**
 * 서버가 준 코드가 **예약을 무효로 만드나.**
 *
 * 만료됐거나 없는 예약으로 ②③ 을 다시 부르면 영원히 같은 오류를 본다. 그때만 처음부터다.
 */
const RESERVATION_LOST = new Set(['upload_expired', 'upload_not_found']);

/** 확정이 이미 끝났다는 뜻의 코드. 다시 올리면 판이 하나 더 생긴다. */
const ALREADY_FINALIZED = new Set(['upload_already_finalized']);

export function resumeStepAfterError(state, code) {
  if (RESERVATION_LOST.has(code)) return UPLOAD_STEP.RESERVE;
  if (ALREADY_FINALIZED.has(code)) return UPLOAD_STEP.FINALIZE;
  return resumeStep(state);
}

/** 처음부터 다시 해야 하는 실패인가 — 화면이 「파일을 다시 고르세요」를 말할 자리. */
export function needsFreshReservation(code) {
  return RESERVATION_LOST.has(code);
}

// ── 고르기 전 검사 ─────────────────────────────────────────────────────────

/**
 * 확장자 → 이 배포가 받는 형식.
 *
 * 브라우저의 `file.type` 을 먼저 믿지 않는다: `.md` 는 대부분 빈 문자열이고, Windows 에서
 * `.txt` 가 `text/plain` 이 아닌 경우도 있다. **서버가 준 표**의 확장자를 먼저 보고,
 * 거기서 못 찾을 때만 `file.type` 을 본다. 어느 쪽으로도 못 정하면 올리지 않는다 —
 * 아무 형식이나 적어 보내면 서버가 매직바이트로 거절할 뿐이다.
 */
export function mediaTypeForFile(file, limits) {
  const allowed = limits?.allowedMediaTypes ?? [];
  const name = String(file?.name ?? '').toLowerCase();
  for (const entry of allowed) {
    for (const extension of entry.extensions ?? []) {
      if (name.endsWith(String(extension).toLowerCase())) return entry.mediaType;
    }
  }
  const declared = String(file?.type ?? '').split(';')[0].trim().toLowerCase();
  const match = allowed.find((entry) => entry.mediaType === declared);
  return match ? match.mediaType : null;
}

export function kindForMediaType(mediaType, limits) {
  return (limits?.allowedMediaTypes ?? []).find((entry) => entry.mediaType === mediaType)?.kind ?? null;
}

/** `<input accept>` 에 넣을 값. 서버가 준 표에서만 만든다 — 우리가 목록을 갖지 않는다. */
export function acceptAttribute(limits) {
  const allowed = limits?.allowedMediaTypes ?? [];
  const parts = [];
  for (const entry of allowed) {
    parts.push(entry.mediaType);
    for (const extension of entry.extensions ?? []) parts.push(extension);
  }
  return parts.join(',');
}

/** 사람에게 보일 허용 형식 한 줄. `.pdf · .png · .jpg …` */
export function allowedExtensionsLabel(limits) {
  const seen = [];
  for (const entry of limits?.allowedMediaTypes ?? []) {
    for (const extension of entry.extensions ?? []) if (!seen.includes(extension)) seen.push(extension);
  }
  return seen.join(' · ');
}

/**
 * 보내기 전에 여기서 먼저 거절한다. **서버가 여전히 최종 판정이다** — 이 검사는 25MiB 를
 * 다 올린 뒤에 413 을 보는 일을 없애려는 것이지, 서버 검사를 대신하지 않는다.
 */
export function validateFileForUpload(file, limits) {
  if (!file) return { ok: false, code: 'no_file', message: '올릴 파일을 고르세요.' };
  if (!limits?.enabled) {
    return { ok: false, code: 'managed_files_disabled', message: '이 배포에서는 파일 보관이 켜져 있지 않습니다.' };
  }
  const size = Number(file.size ?? 0);
  if (!Number.isFinite(size) || size <= 0) {
    return { ok: false, code: 'empty_file', message: '빈 파일은 올릴 수 없습니다.' };
  }
  const mediaType = mediaTypeForFile(file, limits);
  if (mediaType === null) {
    return {
      ok: false,
      code: 'asset_media_type_not_allowed',
      message: `이 배포가 받는 형식이 아닙니다. 받는 것: ${allowedExtensionsLabel(limits)}`,
    };
  }
  const max = Number(limits.maxFileBytes ?? 0);
  if (max > 0 && size > max) {
    return { ok: false, code: 'asset_file_too_large', message: `파일 하나의 상한은 ${max} 바이트입니다.` };
  }
  const remaining = limits.spaceRemainingBytes;
  if (Number.isFinite(remaining) && remaining !== null && size > remaining) {
    return {
      ok: false,
      code: 'space_storage_quota_exceeded',
      message: '이 Space에 남은 보관 공간보다 큽니다.',
    };
  }
  return { ok: true, mediaType, kind: kindForMediaType(mediaType, limits), byteSize: size };
}

/**
 * 파일 이름에서 뽑은 제목. **고치는 것은 사람이다** — 여기서 만드는 것은 첫 값뿐이다.
 * 확장자만 떼고 나머지는 건드리지 않는다(밑줄·날짜도 그 사람이 붙인 이름의 일부다).
 */
export function deriveTitle(filename) {
  const name = String(filename ?? '').trim();
  if (name === '') return '';
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

// ── 진행률 ────────────────────────────────────────────────────────────────

export function progressPercent(loaded, total) {
  const size = Number(total ?? 0);
  if (!Number.isFinite(size) || size <= 0) return null;
  const done = Math.min(Math.max(Number(loaded ?? 0), 0), size);
  return Math.round((done / size) * 100);
}

const PHASE_LABEL = Object.freeze({
  [UPLOAD_PHASE.IDLE]: '올릴 준비가 되었습니다',
  [UPLOAD_PHASE.DIGESTING]: '파일을 확인하는 중',
  [UPLOAD_PHASE.RESERVING]: '올릴 자리를 준비하는 중',
  [UPLOAD_PHASE.TRANSFERRING]: '보내는 중',
  [UPLOAD_PHASE.FINALIZING]: '저장하는 중',
  [UPLOAD_PHASE.ATTACHING]: 'Task에 붙이는 중',
  [UPLOAD_PHASE.DONE]: '끝났습니다',
  [UPLOAD_PHASE.ERROR]: '멈췄습니다',
});

export function uploadPhaseLabel(state) {
  const phase = state?.phase ?? UPLOAD_PHASE.IDLE;
  if (phase === UPLOAD_PHASE.TRANSFERRING) {
    const percent = progressPercent(state?.loaded, state?.file?.size);
    return percent === null ? PHASE_LABEL[phase] : `보내는 중 ${percent}%`;
  }
  return PHASE_LABEL[phase] ?? phase;
}

/** 다시 누르는 버튼에 적을 말. 무엇을 다시 하는지가 버튼에 보여야 한다. */
export function retryLabel(step) {
  return {
    [UPLOAD_STEP.RESERVE]: '처음부터 다시 올리기',
    [UPLOAD_STEP.TRANSFER]: '보내기 다시 시도',
    [UPLOAD_STEP.FINALIZE]: '저장 다시 시도',
    [UPLOAD_STEP.ATTACH]: '붙이기만 다시 시도',
  }[step] ?? '다시 시도';
}

// ── 실패를 사람의 말로 ─────────────────────────────────────────────────────
//
// 두 가지를 함께 정한다: **무슨 일이 났는지**와 **다시 눌러서 될 일인지**.
// 될 리 없는 재시도를 권하지 않는 것이 절반이다 — 형식이 안 맞는 파일은 열 번 눌러도
// 안 되고, 그걸 권하는 화면은 사람의 시간을 쓴다.

const UPLOAD_MESSAGE = Object.freeze({
  upload_expired: '올릴 자리가 시간이 지나 닫혔습니다. 파일을 다시 올려 주세요.',
  upload_not_found: '올리던 자리를 찾을 수 없습니다. 처음부터 다시 올려 주세요.',
  upload_already_finalized: '이미 저장이 끝난 파일입니다.',
  upload_not_transferred: '아직 파일이 다 도착하지 않았습니다. 보내기부터 다시 해 주세요.',
  upload_transfer_in_progress: '같은 파일을 이미 보내는 중입니다. 잠시 기다려 주세요.',
  upload_size_mismatch: '보낸 크기가 처음 알린 크기와 다릅니다. 보내기를 다시 해 주세요.',
  upload_checksum_mismatch: '보내는 동안 파일 내용이 달라졌습니다. 보내기를 다시 해 주세요.',
  asset_media_type_not_allowed: '이 형식은 받지 않습니다.',
  asset_media_type_kind_mismatch: '파일 형식과 종류가 맞지 않습니다.',
  asset_content_type_mismatch: '파일 내용이 확장자와 다릅니다. 파일을 확인해 주세요.',
  asset_filename_invalid: '파일 이름에 쓸 수 없는 문자가 있습니다. 이름을 바꿔 주세요.',
  asset_file_too_large: '파일이 너무 큽니다.',
  asset_source_type_immutable: '이 자료는 다른 방식으로 등록되어 있어 여기에 파일 판을 더할 수 없습니다.',
  asset_kind_immutable: '이 자료의 종류와 달라 새 판이 될 수 없습니다.',
  space_storage_quota_exceeded: '이 Space의 보관 공간이 가득 찼습니다.',
  managed_files_disabled: '이 서버는 파일 보관을 켜 두지 않았습니다.',
  resource_cloud_unavailable: '자료 보관 서비스에 연결할 수 없습니다.',
  human_direct_required: '파일 올리기는 사람이 직접 로그인한 창에서만 할 수 있습니다.',
  forbidden: '이 Space에 파일을 올릴 권한이 없습니다.',
  not_space_member: '이 Space의 구성원이 아닙니다.',
  space_archived: '보관된 Space에는 올릴 수 없습니다.',
  space_not_projected: '이 Space의 정보가 아직 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.',
  too_many_transfers: '지금 올리는 파일이 많습니다. 잠시 후 다시 시도해 주세요.',
  content_length_required: '파일 크기를 알 수 없어 보내지 못했습니다.',
  gateway_unreachable: '연결이 끊겼습니다. 잠시 후 다시 시도해 주세요.',
  gateway_timeout: '시간 안에 끝나지 않았습니다. 잠시 후 다시 시도해 주세요.',
  attach_failed: '파일은 올라갔고, Task에 붙이는 데서 멈췄습니다.',
  invalid_upload_path: '올리는 주소가 올바르지 않습니다.',
});

/** 다시 눌러도 **같은 결과가 나오는** 실패들. 여기 있으면 재시도 버튼을 그리지 않는다. */
const NOT_RETRYABLE = new Set([
  'asset_media_type_not_allowed',
  'asset_media_type_kind_mismatch',
  'asset_content_type_mismatch',
  'asset_filename_invalid',
  'asset_source_type_immutable',
  'asset_kind_immutable',
  'human_direct_required',
  'forbidden',
  'not_space_member',
  'space_archived',
  'managed_files_disabled',
  'resource_cloud_unavailable',
]);

/**
 * 사람이 읽을 한 줄. 모르는 코드면 서버가 준 문장을 그대로 쓴다 — 지어내지 않는다.
 * 크기·형식처럼 **수치가 있어야 뜻이 통하는** 것은 능력 표에서 값을 빌려 붙인다.
 */
export function uploadErrorMessage(code, { limits = null, fallback = null } = {}) {
  const base = UPLOAD_MESSAGE[code] ?? null;
  if (base === null) return fallback ?? '파일을 올리지 못했습니다.';
  if (code === 'asset_media_type_not_allowed' && limits) {
    return `${base} 받는 형식: ${allowedExtensionsLabel(limits)}`;
  }
  if (code === 'asset_file_too_large' && Number.isFinite(limits?.maxFileBytes)) {
    return `${base} 최대 ${byteLabel(limits.maxFileBytes)}까지 올릴 수 있습니다.`;
  }
  return base;
}

/**
 * 붙이기 실패는 **언제나** 다시 할 수 있다: 파일과 판은 이미 서버에 있고, 남은 일은
 * 참조 하나를 만드는 것뿐이다. 보관 공간이 그사이 가득 찼어도 이 일과는 상관이 없다.
 */
export function canRetryUpload(code, step = null) {
  if (step === UPLOAD_STEP.ATTACH) return true;
  return !NOT_RETRYABLE.has(code);
}

/** 바이트를 사람 단위로. `resource-directory.js` 와 같은 규칙이지만 의존을 만들지 않는다. */
function byteLabel(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes}B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)}${units[unit]}`;
}

// ── 경로 검사 ─────────────────────────────────────────────────────────────

/**
 * 예약 응답이 알려 준 경로를 **그대로 믿지 않는다.**
 *
 * 응답이 `contentPath` 를 준다(§2.2). 편하다고 그 문자열을 그대로 fetch 하면, 그 값이
 * 절대 URL 이나 다른 origin 으로 바뀌는 날 세션 쿠키와 CSRF 토큰이 그리로 따라 나간다.
 * 그래서 **모양을 확인하고, 어긋나면 우리가 아는 경로를 쓴다.**
 *
 * @returns 항상 `/product/resource-uploads/<uploadId>/<step>` 꼴의 상대 경로
 */
export function uploadStepPath(step, uploadId, offered = null) {
  const id = String(uploadId ?? '');
  const known = `/product/resource-uploads/${encodeURIComponent(id)}/${step}`;
  if (typeof offered !== 'string' || offered === '') return known;
  // 절대 URL·스킴·프로토콜 상대 주소는 전부 거절한다. 물음표와 fragment 도 받지 않는다.
  if (!/^\/product\/resource-uploads\/[A-Za-z0-9_-]+\/(content|finalize|abort)$/.test(offered)) return known;
  if (offered !== known) return known;
  return offered;
}

/** 이 경로가 우리가 아는 제품 경로인가. 다른 값은 절대 요청 대상이 되지 않는다. */
export function isKnownProductPath(path) {
  return typeof path === 'string' && /^\/product\/[A-Za-z0-9/_-]+$/.test(path);
}
